/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import truncate from "lodash/truncate";
import Link from "next/link";
import { isElectron } from "react-device-detect";
import styled from "styled-components";

import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper/ConditionalWrapper";
import { UnstyledLink } from "src/components/UI/Links";

interface PageThumbnailProps {
  plate: BasePlate;
  isPublic: boolean;
  filterValue?: string;
  shouldTruncate?: boolean;
  disableLink?: boolean;
}

export const PageThumbnailTitle = ({
  plate,
  filterValue,
  shouldTruncate,
  isPublic,
  disableLink,
}: PageThumbnailProps) => {
  const adjustedPageTitle = () => {
    if (filterValue) {
      return plate.snapshot.title.replace(new RegExp(filterValue, "gi"), (match) => `<mark>${match}</mark>`);
    }
    if (shouldTruncate) {
      return truncate(plate.snapshot.title, { length: 29 });
    }
    return plate.snapshot.title;
  };

  return (
    <ConditionalWrapper
      condition={!disableLink}
      wrapper={(children) =>
        isElectron ? (
          <UnstyledLink href={plate.internalLink}>{children}</UnstyledLink>
        ) : (
          <Link href={plate.internalLink}>
            <div>{children}</div>
          </Link>
        )
      }
    >
      <StyledPageTitle
        $isPublic={isPublic}
        dangerouslySetInnerHTML={{ __html: adjustedPageTitle() }}
      ></StyledPageTitle>
    </ConditionalWrapper>
  );
};

export const StyledPageTitle = styled.div<{ $isPublic: boolean }>`
  margin-top: 5px;
  font-family: "IBM Plex Mono";
  text-align: right;
  color: ${({ theme, $isPublic }) => ($isPublic ? theme.text.default : theme.accent)};
  word-break: break-word;
  flex-shrink: 0;
`;
